import { useIonModal } from '@ionic/react'
import { AnimatePresence, motion } from 'framer-motion'

import { useGetPortalNotes } from '@apps-common/apis/portal/portal-note'
import { NoteItemCard, NoteItemSkeletonCard } from '@apps-common/apps/portal/pages/units/pages/manage/components/notes'
import { PortalNoteModal } from '@apps-common/apps/portal/pages/units/pages/manage/pages/notes/components'
import { NoResultsAddCard } from '@apps-common/components/common'
import { PullRefresh } from '@apps-common/components/common/PullRefresh'
import { LayoutPadding } from '@apps-common/components/layout'
import { ModalDismiss } from '@apps-common/config/modal'
import { motionFadeIn } from '@apps-common/config/motion-variants'
import { sortNumerically } from '@apps-common/helpers/array-sort'
import { modalSlideUpEnterAnimation, modalSlideUpLeaveAnimation } from '@apps-common/lib/modal-animations'

interface IProps {
  paddingTop: string
  entityId: string
}

export const PortalNotes: React.FC<IProps> = ({ paddingTop, entityId }) => {
  const { data, isLoading, isRefetching, isError, refetch } = useGetPortalNotes({
    config: {
      enabled: entityId !== undefined
    },
    entityId
  })

  const [present, dismiss] = useIonModal(PortalNoteModal, {
    componentProps: {
      entityId
    },
    onDismiss: () => {
      dismiss(null, ModalDismiss.Confirm)
    },
    enableBackdropDismiss: true
  })

  const loading = isLoading || isError

  return (
    <>
      <PullRefresh loading={isRefetching} callback={() => refetch()} />
      <LayoutPadding classes={paddingTop}>
        <div className='grid grid-cols-1 lg:grid-cols-2 gap-[10px]'>
          <AnimatePresence mode='popLayout'>
            {loading &&
              Array.from(Array(4).keys()).map((n, i) => (
                <motion.div key={`management-notes-skeleton-${i}`} {...motionFadeIn(0.2)}>
                  <NoteItemSkeletonCard />
                </motion.div>
              ))}
            {!loading && data && data?.length === 0 && (
              <motion.div key='no-portal-notes-create-button' {...motionFadeIn(0.25)}>
                <NoResultsAddCard
                  title='No notes'
                  message='Add a new note to device.'
                  disabled={loading}
                  callback={() =>
                    present({
                      enterAnimation: modalSlideUpEnterAnimation,
                      leaveAnimation: modalSlideUpLeaveAnimation,
                      backdropDismiss: true
                    })
                  }
                />
              </motion.div>
            )}
            {!loading &&
              data &&
              data?.length > 0 &&
              data
                ?.sort((a, b) => sortNumerically(a, b, 'portalNoteId'))
                .map((n, i) => (
                  <motion.div key={`portal-notes-${n.portalNoteId}-${i}`} {...motionFadeIn(0.2)}>
                    <NoteItemCard note={n} />
                  </motion.div>
                ))}
          </AnimatePresence>
        </div>
      </LayoutPadding>
    </>
  )
}
